import {
  ArrowsRightLeftIcon,
  ChatBubbleBottomCenterTextIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline'
import { cn } from '@duck/ui'
import { Link } from '@tanstack/react-router'
import { useTar } from '@/modules'
import { trackMarketing } from '@/modules/marketingAnalytics'
import { GuideLastUpdated, IntentRelatedLinks } from './IntentRelatedLinks'

const ANALYTICAL_TASK_STEP_CARDS = [
  {
    titleKey: 'solvingAnalyticalTasksPage.stepCards.clarify.title',
    descriptionKey: 'solvingAnalyticalTasksPage.stepCards.clarify.description',
    icon: ChatBubbleBottomCenterTextIcon,
  },
  {
    titleKey: 'solvingAnalyticalTasksPage.stepCards.inspect.title',
    descriptionKey: 'solvingAnalyticalTasksPage.stepCards.inspect.description',
    icon: MagnifyingGlassIcon,
  },
  {
    titleKey: 'solvingAnalyticalTasksPage.stepCards.decide.title',
    descriptionKey: 'solvingAnalyticalTasksPage.stepCards.decide.description',
    icon: ArrowsRightLeftIcon,
  },
  {
    titleKey: 'solvingAnalyticalTasksPage.stepCards.explain.title',
    descriptionKey: 'solvingAnalyticalTasksPage.stepCards.explain.description',
    icon: MegaphoneIcon,
  },
] as const

const ANALYTICAL_TASK_CHECKLIST = [
  'solvingAnalyticalTasksPage.checklist.scope',
  'solvingAnalyticalTasksPage.checklist.grain',
  'solvingAnalyticalTasksPage.checklist.edgeCases',
  'solvingAnalyticalTasksPage.checklist.alternatives',
  'solvingAnalyticalTasksPage.checklist.communication',
] as const

const ANALYTICAL_TASK_OUTPUT_COLUMNS = [
  {
    titleKey: 'solvingAnalyticalTasksPage.outputCards.answerTitle',
    descriptionKey: 'solvingAnalyticalTasksPage.outputCards.answerDescription',
  },
  {
    titleKey: 'solvingAnalyticalTasksPage.outputCards.confidenceTitle',
    descriptionKey: 'solvingAnalyticalTasksPage.outputCards.confidenceDescription',
  },
] as const

export const AnalyticalWorkflowPage = () => {
  const { t } = useTar()

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
        <section
          className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-10 px-6 py-8 md:px-10 md:py-12"
          aria-labelledby="analytical-heading"
        >
          <div className="flex flex-col items-start gap-6 max-w-xl">
            <h1
              id="analytical-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
            >
              {t('solvingAnalyticalTasksPage.title')}
              <span className="text-accent">.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('solvingAnalyticalTasksPage.subtitle')}
            </p>
            <GuideLastUpdated isoDate="2026-05-09" />
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
              {t('solvingAnalyticalTasksPage.introP1')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('solvingAnalyticalTasksPage.introP2')}
            </p>
          </div>

          <aside className="rounded-xl border border-border bg-card/60 p-6 md:mt-8">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
              {t('solvingAnalyticalTasksPage.sidebarEyebrow')}
            </p>
            <p className="text-sm leading-7 text-foreground/90">
              {t('solvingAnalyticalTasksPage.sidebarBody')}
            </p>
          </aside>
        </section>

        <section className="space-y-8" aria-labelledby="steps-heading">
          <div className="space-y-3">
            <h2
              id="steps-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('solvingAnalyticalTasksPage.stepsTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('solvingAnalyticalTasksPage.stepsDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
            {ANALYTICAL_TASK_STEP_CARDS.map((card, index) => {
              const Icon = card.icon
              const isXlLeftEdge = index === 0
              const isMdRowStart = index % 2 === 0
              const isMdTopRow = index < 2
              return (
                <div
                  key={card.titleKey}
                  className={cn(
                    'px-6 py-5 space-y-3',
                    'md:py-2',
                    !isMdRowStart && 'md:border-s md:border-border/50',
                    !isMdTopRow && 'md:border-t md:border-border/50 md:pt-6',
                    'xl:border-s xl:border-border/50 xl:border-t-0 xl:pt-2',
                    isXlLeftEdge && 'xl:border-s-0'
                  )}
                >
                  <h3 className="flex items-baseline gap-2 text-lg font-semibold text-foreground">
                    <Icon aria-hidden className="h-4 w-4 self-center text-accent/70" />
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {t(card.descriptionKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-7" aria-labelledby="checklist-heading">
          <div className="space-y-3">
            <h2
              id="checklist-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('solvingAnalyticalTasksPage.checklistTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('solvingAnalyticalTasksPage.checklistDescription')}
            </p>
          </div>

          <ol className="divide-y divide-border/50">
            {ANALYTICAL_TASK_CHECKLIST.map((key, index) => (
              <li key={key} className="flex items-start gap-4 py-4">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 ring-1 ring-accent/20 text-sm font-semibold text-accent tabular-nums">
                  {index + 1}
                </span>
                <p className="text-base leading-7 text-muted-foreground pt-1">{t(key)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="relative isolate border-y border-border py-14 md:py-20"
          aria-labelledby="output-heading"
        >
          <PresentationChartLineIcon
            aria-hidden
            className="pointer-events-none absolute -end-12 -top-10 h-72 w-72 text-accent/[0.13] -rotate-6 select-none"
          />
          <div className="relative space-y-8">
            <div className="space-y-3 max-w-2xl">
              <h2
                id="output-heading"
                className="text-3xl md:text-4xl font-bold tracking-tight text-foreground"
              >
                {t('solvingAnalyticalTasksPage.outputTitle')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('solvingAnalyticalTasksPage.outputDescription')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2">
              {ANALYTICAL_TASK_OUTPUT_COLUMNS.map((column, index) => (
                <div
                  key={column.titleKey}
                  className={cn(
                    'px-6 py-5 md:py-3 space-y-2',
                    index !== 0 && 'md:border-s md:border-border/50'
                  )}
                >
                  <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug">
                    {t(column.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(column.descriptionKey)}
                  </p>
                </div>
              ))}
            </div>

            <div className="px-6 md:px-0">
              <Link
                to="/challenges/available"
                onClick={() => trackMarketing('guide:cta:click', { guide: 'analytical_workflow' })}
                className="group inline-flex items-center gap-2 rounded-lg bg-accent hover:bg-accent/85 text-accent-foreground text-base font-semibold px-5 py-3 shadow-[0_0_40px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5"
              >
                {t('solvingAnalyticalTasksPage.ctaLabel')}
                <span
                  aria-hidden
                  className="ms-1 inline-block transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                >
                  →
                </span>
              </Link>
            </div>
          </div>
        </section>

        <IntentRelatedLinks
          source="analytical_workflow"
          links={[
            { to: '/xp-lab-feedback', label: t('intentResources.feedbackTitle') },
            { to: '/using-ai-without-outsourcing-judgment', label: t('intentResources.aiJudgmentTitle') },
            { to: '/take-home-assignments', label: t('intentResources.takeHomeAssignmentsTitle') },
          ]}
        />
      </div>
    </div>
  )
}
